import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { FileText, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { MEGA_MENU, HINDI_LINK, type NavLink } from "@/data/navigation";

/* Shared interaction recipes. Keeping them in one place stops the header from
   drifting into six slightly different hover treatments. */

/** Quiet nav item: legible at rest, a soft neutral pad on hover. Deliberately
    carries no radius so it can sit on a <Button> without fighting the button
    chassis' own rounding. */
const NAV_ITEM =
  "text-sm font-medium text-muted-foreground transition-colors duration-180 ease-premium hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground";

/** Row inside the mega menu / mobile sheet. */
const PANEL_LINK =
  "rounded-md text-sm text-muted-foreground transition-colors duration-180 ease-premium hover:bg-muted hover:text-foreground focus-visible:bg-muted focus-visible:text-foreground";

/** Small caps label above a group of links. */
const GROUP_HEADING =
  "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

export function SiteHeader() {
  const location = useLocation();
  const { user, signOut, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/85">
      <nav aria-label="Main navigation">
        <div className="container mx-auto flex items-center justify-between gap-2 h-16 px-4 min-w-0">
          <Link
            to="/"
            className="flex items-center gap-2.5 shrink-0 rounded-md py-1.5 pr-1 transition-opacity duration-180 ease-premium hover:opacity-85"
          >
            <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center shadow-xs">
              <FileText className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display text-base sm:text-xl font-bold tracking-tight text-foreground">AI Resume Studio</span>
          </Link>

          {/* Desktop mega menu */}
          <NavigationMenu className="hidden lg:flex min-w-0 shrink">
            <NavigationMenuList className="gap-1 space-x-0">
              {MEGA_MENU.map((menu) => (
                <NavigationMenuItem key={menu.label}>
                  <NavigationMenuTrigger className="bg-transparent">
                    {menu.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[620px] p-5">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                        {menu.columns.map((col) => (
                          <div key={col.heading}>
                            <p className={cn(GROUP_HEADING, "px-3 pt-1 pb-2.5")}>
                              {col.heading}
                            </p>
                            <ul className="space-y-0.5">
                              {col.links.map((l) => (
                                <li key={l.to + l.label}>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={l.to}
                                      title={l.title}
                                      className={cn(
                                        PANEL_LINK,
                                        "block px-3 py-2 leading-snug",
                                        l.hindi && "font-medium text-foreground"
                                      )}
                                    >
                                      {l.label}
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      {menu.featured && (
                        <div className="mt-4 border-t border-border pt-3">
                          <NavigationMenuLink asChild>
                            <Link
                              to={menu.featured.to}
                              className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-primary transition-colors duration-180 ease-premium hover:bg-primary-soft focus-visible:bg-primary-soft"
                            >
                              {menu.featured.label} →
                            </Link>
                          </NavigationMenuLink>
                        </div>
                      )}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ))}
              <NavigationMenuItem>
                <NavigationMenuLink asChild>
                  <Link
                    to="/features"
                    className={cn(
                      NAV_ITEM,
                      "inline-flex h-9 items-center rounded-md px-3",
                      location.pathname === "/features" && "bg-muted text-foreground"
                    )}
                  >
                    Features
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
            {/* Prominent Hindi entry point */}
            <Button variant="ghost" size="sm" asChild className={cn(NAV_ITEM, "hidden md:inline-flex h-9 px-3")}>
              <Link to={HINDI_LINK.to} title={HINDI_LINK.title} lang="hi">हिंदी</Link>
            </Button>
            <ThemeToggle />
            {loading ? null : user ? (
              <>
                <Button variant="ghost" size="sm" asChild className={cn(NAV_ITEM, "hidden sm:inline-flex h-9 px-3")}>
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={signOut}
                  aria-label="Sign out"
                  className={cn(NAV_ITEM, "h-9 w-9 px-0")}
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild className={cn(NAV_ITEM, "hidden sm:inline-flex h-9 px-3")}>
                  <Link to="/login" rel="nofollow">Log In</Link>
                </Button>
                <Button size="sm" asChild className="hidden sm:inline-flex px-4">
                  <Link to="/signup">Get Started</Link>
                </Button>
              </>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(NAV_ITEM, "lg:hidden h-11 w-11 -mr-1")}
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="flex w-full flex-col gap-0 border-l border-border p-0 sm:p-0 sm:max-w-sm"
              >
                <div className="flex h-16 shrink-0 items-center border-b border-border px-5">
                  <SheetTitle className="text-left text-base font-semibold">Menu</SheetTitle>
                </div>

                <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-1">
                  <Accordion type="multiple" className="w-full">
                    {MEGA_MENU.map((menu) => (
                      <AccordionItem key={menu.label} value={menu.label} className="border-b border-border last:border-b-0">
                        <AccordionTrigger className="rounded-md px-3 text-sm font-semibold transition-colors duration-180 ease-premium hover:bg-muted">
                          {menu.label}
                        </AccordionTrigger>
                        <AccordionContent className="pb-4 pt-0">
                          <div className="flex flex-col gap-5">
                            {menu.columns.map((col) => (
                              <div key={col.heading}>
                                <p className={cn(GROUP_HEADING, "px-3 mb-1")}>
                                  {col.heading}
                                </p>
                                <div className="flex flex-col">
                                  {col.links.map((l: NavLink) => (
                                    <Link
                                      key={l.to + l.label}
                                      to={l.to}
                                      onClick={close}
                                      className={cn(
                                        PANEL_LINK,
                                        "flex min-h-11 items-center px-3 leading-snug active:bg-muted",
                                        l.hindi && "font-medium text-foreground"
                                      )}
                                    >
                                      {l.label}
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                <div className="shrink-0 border-t border-border bg-surface-sunken px-2 py-3 flex flex-col gap-1">
                  <Link
                    to="/features"
                    onClick={close}
                    className={cn(PANEL_LINK, "flex min-h-11 items-center px-3 text-foreground active:bg-muted")}
                  >
                    Features
                  </Link>
                  <Link
                    to={HINDI_LINK.to}
                    onClick={close}
                    lang="hi"
                    className={cn(PANEL_LINK, "flex min-h-11 items-center px-3 font-medium text-foreground active:bg-muted")}
                  >
                    हिंदी — Resume Kaise Banaye
                  </Link>
                  {user ? (
                    <Link
                      to="/dashboard"
                      onClick={close}
                      className={cn(PANEL_LINK, "flex min-h-11 items-center px-3 text-foreground active:bg-muted")}
                    >
                      Dashboard
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        rel="nofollow"
                        onClick={close}
                        className={cn(PANEL_LINK, "flex min-h-11 items-center px-3 text-foreground active:bg-muted")}
                      >
                        Log In
                      </Link>
                      <Link
                        to="/signup"
                        onClick={close}
                        className="mt-1 flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-primary transition-colors duration-180 ease-premium hover:bg-primary-hover"
                      >
                        Get Started
                      </Link>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
}
